// Imports
import { darken } from 'polished';
import React, { useState, useEffect } from 'react';
import styled from 'styled-components/macro';
import { styling } from '../../../../../../shareables/foundation/front-end/components/buttons/standard';
import { useAppDispatch } from '../../../../../../shareables/foundation/front-end/redux/hooks';
import apiActions from '../../../../../../shareables/integrations/api/redux/actions';
import { GetCommunicationsResponse } from '../../../../../../shareables/types/api/main/website/communications/get';
import parseAndSanitizeMarkdown from '../../../../../../shareables/utils/markdown/parse-and-sanitize';
import Attachments from '../../page-types/shared-components/attachments';
import { StyledEditorGeneratedContentContainer } from '../../page-types/shared-components/styled-editor-generated-content-container';
import PageTitle from '../../shared-components/page-title';


// Styled components
const Container = styled.div`
	padding: 0.9375rem;
	margin-bottom: 1.25rem;
	border: 0.0625rem solid #ddd;
	border-radius: 0.25rem;
	box-shadow: 0 0.0625rem 0.0625rem rgba(0, 0, 0, 0.05);
`;

const CommunicationHeading = styled.h3`
	display: flex;
	justify-content: space-between;
	margin-bottom: 0.625rem;
	align-items: baseline;
	font-size: 1.5rem;
	font-weight: 500;
	line-height: 1.1;
`;

const DateWrapper = styled.span`
	font-weight: normal;
	font-size: 0.75rem;
`;

const CommunicationContent = styled(StyledEditorGeneratedContentContainer)`
	line-height: 1.3125rem;
	width: 100%;
	
	a.btn-primary {
		${styling}
		
		background-color: ${(props) => props.theme.baseColor};
		
		&:hover,
		&:focus {
			background-color: ${(props) => darken(0.05, props.theme.baseColor)} !important;
		}
	}
`;

const Divider = styled.hr`
	margin-top: 0.625rem;
	margin-bottom: 0.625rem;
	border: 0;
	border-top: 0.0625rem solid #eee;
`;

const AttachmentsWrapper = styled.div`
	font-size: 90%;
`;


// Define the accepted props
interface Props {
	storybookCommunications?: GetCommunicationsResponse;
}


// Function component
const CommunicationsArchive: React.FC<Props> = ({ storybookCommunications }) => {
	// Use state
	const [communications, setCommunications] = useState<GetCommunicationsResponse | null>(
		storybookCommunications ?? null
	);
	
	
	// Use Redux functionality
	const dispatch = useAppDispatch();
	
	
	// Load communications on mount
	useEffect(() => {
		void dispatch(
			apiActions.call<undefined, undefined, GetCommunicationsResponse>({
				action: 'GET',
				uri: `/website/${window.location.hostname}/communications`,
				completion: (json) => {
					setCommunications(json.response);
				},
			})
		);
	}, [dispatch]);
	
	
	// If loading, then display nothing
	if (communications === null) {
		return null;
	}
	
	
	// Return JSX
	return (
		<React.Fragment>
			<PageTitle>Communications archive</PageTitle>
			
			{communications.length === 0 ? (
				<em>You have not received any communications yet…</em>
			) : (
				communications.map((communicationArchive) => (
					<Container key={communicationArchive.id}>
						<CommunicationHeading>
							{communicationArchive.type === 'EMAIL'
								? communicationArchive.subject
								: communicationArchive.type === 'LETTER'
									? 'Mailed letter'
									: 'Text message'}
							
							<DateWrapper>
								{new Date(communicationArchive.sent ?? '').toLocaleDateString(undefined, {
									month: 'short',
									day: 'numeric',
									year: 'numeric',
								})}{' '}
								<small className='is-gray'>
									{new Date(communicationArchive.sent ?? '').toLocaleTimeString(undefined, {
										hour: 'numeric',
										minute: 'numeric',
									})}
								</small>
							</DateWrapper>
						</CommunicationHeading>
						
						{communicationArchive.type === 'LETTER' ? (
							// eslint-disable-next-line react/jsx-no-target-blank
							<a
								target='_blank'
								href={`https://${window.location.hostname}/private-files/letter/${communicationArchive.id}/${communicationArchive.content ?? ''}`}
							>
								View letter
							</a>
						) : (
							<CommunicationContent
								dangerouslySetInnerHTML={{ __html: parseAndSanitizeMarkdown(communicationArchive.content ?? '') }}
							/>
						)}
						
						{communicationArchive.attachments && (
							<React.Fragment>
								<Divider />
								<AttachmentsWrapper>
									<Attachments id={communicationArchive.id} attachments={communicationArchive.attachments} />
								</AttachmentsWrapper>
							</React.Fragment>
						)}
					</Container>
				))
			)}
		</React.Fragment>
	);
};

export default CommunicationsArchive;
